import React, { useEffect, useState } from 'react';
import { Modal, Spinner, Alert, Table } from 'react-bootstrap';
import styled, { css } from 'styled-components';
import axios from 'axios';
import constants from '../../const.js';

export const infoStyles = css`
  .info-section {
    background: white;
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 14px;
  }

  .info-section h6 {
    font-weight: 700;
    color: #667eea;
    margin-bottom: 8px;
  }

  .info-table td {
    padding: 4px 8px;
    vertical-align: top;
  }

  .info-table td:first-child {
    font-weight: 600;
    width: 40%;
    color: #555;
  }
`;

const StyledModal = styled(Modal)`
  /* Let the gallery stay interactive behind the dialog so hover is not blocked */
  pointer-events: none;

  .modal-dialog {
    pointer-events: auto;
    max-width: 700px;
    width: 95vw;
  }

  .modal-content {
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    border: none;
    border-radius: 12px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    overflow: hidden;
  }

  .modal-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    padding: 12px 20px;
  }

  .modal-header .modal-title {
    font-weight: 700;
    font-size: 18px;
    color: white;
  }

  .modal-header .btn-close {
    filter: invert(1);
  }

  .modal-body {
    padding: 20px;
  }

  ${infoStyles}
`;

const formatBytes = (bytes) => {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const formatExifValue = (field, value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (value instanceof Date) return value.toLocaleString();
  switch (field) {
    case 'ExposureTime':
      return typeof value === 'number' && value < 1 ? `1/${Math.round(1 / value)} s` : `${value} s`;
    case 'FNumber':
      return `f/${value}`;
    case 'FocalLength':
      return `${value} mm`;
    case 'latitude':
    case 'longitude':
      return Number(value).toFixed(6);
    default:
      return String(value);
  }
};

const EXIF_LABELS = {
  Make: 'Camera make',
  Model: 'Camera model',
  LensModel: 'Lens',
  DateTimeOriginal: 'Date taken',
  CreateDate: 'Created (EXIF)',
  ModifyDate: 'Modified (EXIF)',
  ExposureTime: 'Exposure',
  FNumber: 'Aperture',
  ISO: 'ISO',
  FocalLength: 'Focal length',
  Software: 'Software',
  Orientation: 'Orientation',
  ImageDescription: 'Description',
  Artist: 'Artist',
  Copyright: 'Copyright',
  latitude: 'Latitude',
  longitude: 'Longitude'
};

const Row = ({ label, value }) => (
  <tr>
    <td>{label}</td>
    <td>{value}</td>
  </tr>
);

export const ImageInfoContent = ({ galleryId, albumName, fileName }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fileInfo, setFileInfo] = useState(null);
  const [dbInfo, setDbInfo] = useState(null);
  const [dbMissing, setDbMissing] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      setFileInfo(null);
      setDbInfo(null);
      setDbMissing(false);

      const token = localStorage.getItem('token') || '';
      const tokenSliced = token.slice(7);

      let contentApiBaseUrl = constants.CONTENT_DOMAIN;
      if (contentApiBaseUrl && contentApiBaseUrl.includes('/download')) {
        contentApiBaseUrl = contentApiBaseUrl.split('/download')[0];
      } else if (!contentApiBaseUrl) {
        contentApiBaseUrl = 'http://localhost:444';
      }

      const dbFilePath = `${galleryId}/${albumName}/${fileName}`;

      const fileRequest = axios.get(
        `${contentApiBaseUrl}files/gallery/${encodeURIComponent(galleryId)}/image-info?albumName=${encodeURIComponent(albumName)}&fileName=${encodeURIComponent(fileName)}&token=${encodeURIComponent(tokenSliced)}`
      );
      const dbRequest = axios.get(
        `${constants.DATA1_API_URL}/gallery/image/${encodeURIComponent(dbFilePath)}`
      ).then(res => ({ data: res.data, missing: false }))
        .catch(err => {
          if (err.response && err.response.status === 404) return { data: null, missing: true };
          throw err;
        });

      const [fileResult, dbResult] = await Promise.allSettled([fileRequest, dbRequest]);
      if (cancelled) return;

      if (fileResult.status === 'fulfilled') {
        setFileInfo(fileResult.value.data);
      } else {
        setError(fileResult.reason?.response?.data?.message || 'Failed to load file information');
      }

      if (dbResult.status === 'fulfilled') {
        setDbInfo(dbResult.value.data);
        setDbMissing(dbResult.value.missing);
      } else {
        setDbMissing(false);
      }

      setLoading(false);
    };

    load();
    return () => { cancelled = true; };
  }, [galleryId, albumName, fileName]);

  const exifEntries = fileInfo?.exif ? Object.entries(fileInfo.exif) : [];

  return (
    <>
        {loading && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spinner animation="border" size="sm" /> Loading image info…
          </div>
        )}

        {!loading && error && <Alert variant="danger">{error}</Alert>}

        {!loading && fileInfo && (
          <>
            <div className="info-section">
              <h6>File</h6>
              <Table size="sm" className="info-table mb-0" borderless>
                <tbody>
                  <Row label="Name" value={fileInfo.fileName} />
                  <Row label="Album" value={fileInfo.albumName} />
                  <Row label="Size" value={formatBytes(fileInfo.fileSize)} />
                  <Row
                    label="Dimensions"
                    value={fileInfo.dimensions ? `${fileInfo.dimensions.width} × ${fileInfo.dimensions.height}px` : '—'}
                  />
                  <Row label="Format" value={fileInfo.dimensions?.type ? fileInfo.dimensions.type.toUpperCase() : '—'} />
                  <Row label="Created" value={formatDate(fileInfo.created)} />
                  <Row label="Modified" value={formatDate(fileInfo.modified)} />
                </tbody>
              </Table>
            </div>

            <div className="info-section">
              <h6>Metadata (EXIF)</h6>
              {exifEntries.length === 0 ? (
                <div style={{ color: '#888', fontStyle: 'italic' }}>No EXIF metadata in this file</div>
              ) : (
                <Table size="sm" className="info-table mb-0" borderless>
                  <tbody>
                    {exifEntries.map(([field, value]) => (
                      <Row
                        key={field}
                        label={EXIF_LABELS[field] || field}
                        value={formatExifValue(field, value)}
                      />
                    ))}
                  </tbody>
                </Table>
              )}
            </div>

            <div className="info-section">
              <h6>Database</h6>
              {dbMissing && <div style={{ color: '#888', fontStyle: 'italic' }}>Not in database</div>}
              {!dbMissing && dbInfo && (
                <Table size="sm" className="info-table mb-0" borderless>
                  <tbody>
                    <Row label="Title" value={dbInfo.title || '—'} />
                    <Row label="Description" value={dbInfo.description || '—'} />
                    <Row label="Actors linked" value={Array.isArray(dbInfo.actors) ? dbInfo.actors.length : '—'} />
                    <Row label="Tags linked" value={Array.isArray(dbInfo.tags) ? dbInfo.tags.length : '—'} />
                    <Row label="Positions tagged" value={Array.isArray(dbInfo.positions) ? dbInfo.positions.length : '—'} />
                  </tbody>
                </Table>
              )}
              {!dbMissing && !dbInfo && <div style={{ color: '#888', fontStyle: 'italic' }}>Database unavailable</div>}
            </div>
          </>
        )}
    </>
  );
};

const ImageInfoModal = ({ galleryId, albumName, fileName, imageUrl, onClose, onHoverEnter, onHoverLeave }) => (
  <StyledModal show onHide={onClose} centered backdrop={false} enforceFocus={false}>
    <div onMouseEnter={onHoverEnter} onMouseLeave={onHoverLeave}>
      <Modal.Header closeButton>
        <Modal.Title>Image Info</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {imageUrl && (
          <div style={{ textAlign: 'center', marginBottom: '14px' }}>
            <img
              src={imageUrl}
              alt={fileName}
              style={{ maxWidth: '100%', maxHeight: '260px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}
            />
          </div>
        )}
        <ImageInfoContent galleryId={galleryId} albumName={albumName} fileName={fileName} />
      </Modal.Body>
    </div>
  </StyledModal>
);

export default ImageInfoModal;
